import { useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Table, Input, InputNumber, Select, Button, Modal, Form, Tooltip, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useTasks, useUpdateTask, useTransitionTaskStatus, useCreateTask } from "../../queries/tasks";
import { useProject } from "../../queries/projects";
import { useUsers } from "../../queries/users";
import { TaskProps, SessionUser } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { ProjectNav } from "../../components/ui";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { TaskPriorityTag } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import AuthContext from "../../context/AuthContext";
import styles from "./backlog.module.css";

function isReadyEligible(task: TaskProps): boolean {
  return !!task.description && task.storyPoints != null && !!task.assigneeId;
}

const saveErrorMessage = (err: unknown) =>
  err instanceof ApiError ? err.message : "Could not save the change";

/**
 * The grooming table. Unlike the other lists this one is *for* editing, so the
 * cells stay live inputs — the design work is making the three readiness
 * requirements legible at a glance instead of hiding them behind a disabled
 * button with no explanation.
 */
export const BacklogPage = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const { userData } = useContext(AuthContext);
  const isStaff = userData?.role === "admin" || userData?.role === "user";
  const { data: tasks, isLoading, isError, error } = useTasks({ projectId: projectId!, status: "backlog" });
  const { data: project } = useProject(projectId!);
  const { data: users } = useUsers({ enabled: isStaff });
  const updateTask = useUpdateTask(projectId!);
  const transitionStatus = useTransitionTaskStatus(projectId!);
  const createTask = useCreateTask(projectId!);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [form] = Form.useForm();

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (tasks || []).filter((t: TaskProps) => (q ? t.title.toLowerCase().includes(q) : true));
  }, [tasks, query]);

  const readyCount = (tasks || []).filter(isReadyEligible).length;

  const handleMoveToReady = async (task: TaskProps) => {
    try {
      await transitionStatus.mutateAsync({ id: task._id, status: "ready" });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not move task to Ready");
    }
  };

  const handleCreateTask = async () => {
    const values = await form.validateFields();
    try {
      await createTask.mutateAsync({ title: values.title });
      setIsModalOpen(false);
      form.resetFields();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not create the task");
    }
  };

  const Readiness = ({ task }: { task: TaskProps }) => {
    const checks: [string, boolean][] = [
      ["Description", !!task.description],
      ["Points", task.storyPoints != null],
      ["Assignee", !!task.assigneeId],
    ];
    const done = checks.filter(([, ok]) => ok).length;
    return (
      <Tooltip title={checks.filter(([, ok]) => !ok).map(([l]) => l).join(", ") || "Ready to move"}>
        <span className={styles.readiness}>
          {checks.map(([label, ok]) => (
            <span key={label} className={`${styles.pip} ${ok ? styles.pipOn : ""}`} />
          ))}
          <span className="q-num">{done}/3</span>
        </span>
      </Tooltip>
    );
  };

  return (
    <PageShell
      kind="list"
      crumbs={[{ label: "Projects", to: "/projects" }, { label: project?.name || "Project" }]}
      title="Backlog"
      meta={[
        { label: "Waiting", value: <span className="q-num">{(tasks || []).length}</span> },
        { label: "Ready to move", value: <span className="q-num" style={{ color: readyCount ? "var(--q-ok)" : undefined }}>{readyCount}</span> },
      ]}
      actions={<Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>New task</Button>}
      tabs={<ProjectNav projectId={projectId!} active="backlog" />}
    >
      <div className={styles.hint}>
        <span className={styles.hintKey}>Grooming</span>
        <span>A task needs a description, story points and an assignee before it can move to Ready.</span>
      </div>

      <Toolbar count={rows.length} unit="tasks">
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "var(--q-text-faint)" }} />}
          placeholder="Search titles"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 240 }}
        />
      </Toolbar>

      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<TaskProps>
          rowKey="_id"
          size="small"
          dataSource={rows}
          pagination={rows.length > 25 ? { pageSize: 25, size: "small" } : false}
          /* fixed columns total 840px; without this the flexible Description
             column collapses on a 1280px screen once the sidebar is out */
          scroll={{ x: 1100 }}
          locale={{
            emptyText: (
              <EmptyState
                title="The backlog is empty"
                description="Add a task here, or promote a support ticket — either way it lands in this list to be groomed."
                actions={<Button type="primary" onClick={() => setIsModalOpen(true)}>New task</Button>}
              />
            ),
          }}
          columns={[
            {
              title: "Title", dataIndex: "title", width: 260,
              render: (title: string) => <span style={{ fontWeight: 500 }}>{title}</span>,
            },
            {
              title: "Description", dataIndex: "description",
              render: (value, task) => (
                <Input
                  size="small"
                  bordered={false}
                  className={styles.filledField}
                  placeholder="What does done look like?"
                  defaultValue={value}
                  onBlur={(e) =>
                    updateTask.mutate(
                      { id: task._id, data: { description: e.target.value } },
                      { onError: (err) => message.error(saveErrorMessage(err)) }
                    )
                  }
                />
              ),
            },
            {
              title: "Priority", dataIndex: "priority", width: 100,
              render: (p: TaskProps["priority"]) => <TaskPriorityTag priority={p} />,
            },
            {
              title: "Points", dataIndex: "storyPoints", width: 90,
              render: (value, task) => (
                <InputNumber
                  size="small"
                  bordered={false}
                  className={styles.filledField}
                  style={{ width: "100%" }}
                  min={0}
                  defaultValue={value ?? undefined}
                  onBlur={(e) => {
                    const parsed = Number(e.target.value);
                    updateTask.mutate(
                      { id: task._id, data: { storyPoints: e.target.value === "" || Number.isNaN(parsed) ? null : parsed } },
                      { onError: (err) => message.error(saveErrorMessage(err)) }
                    );
                  }}
                />
              ),
            },
            {
              title: "Assignee", dataIndex: "assigneeId", width: 170,
              render: (value, task) => (
                <Select
                  size="small"
                  bordered={false}
                  className={styles.filledField}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ width: "100%" }}
                  placeholder="Unassigned"
                  defaultValue={value ?? undefined}
                  options={(users || []).map((u: SessionUser) => ({ label: u.name, value: u._id }))}
                  onChange={(v) =>
                    updateTask.mutate(
                      { id: task._id, data: { assigneeId: v || null } },
                      { onError: (err) => message.error(saveErrorMessage(err)) }
                    )
                  }
                />
              ),
            },
            { title: "Ready", width: 90, render: (_, task) => <Readiness task={task} /> },
            {
              title: "", width: 130, align: "right",
              render: (_, task) => (
                <Button size="small" disabled={!isReadyEligible(task)} onClick={() => handleMoveToReady(task)}>
                  Move to Ready
                </Button>
              ),
            },
          ]}
        />
      </DisplayComponent>

      <Modal
        title="New task"
        open={isModalOpen}
        onOk={handleCreateTask}
        okText="Add to backlog"
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={createTask.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Short, action-shaped" />
          </Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
};
